import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAbruf, type Seite } from '../api'
import { Karte, Merkmal, Tabelle, Zustand } from '../bausteine'
import { datum, menge } from '../formate'
import { Statuslegende, useStatuserklaerungen } from '../vertragsstatus'

interface Vertragszeile {
  id: string
  vertragsnummer: string
  kundenname: string | null
  marktlokation: string | null
  status: string
  lieferbeginn: string
  lieferende: string | null
}

/* Alle Werte ausser dem Altwert ABGELEHNT (vor V53), in der Reihenfolge des
 * Lebenszyklus; ABGERECHNET seit dem 19.10.2026. */
// Aufzaehlung: Vertragsstatus
const zustaende = [
  'ANGELEGT',
  'ANGEMELDET',
  'ANMELDUNG_ERFORDERLICH',
  'IN_BELIEFERUNG',
  'GEKUENDIGT',
  'BEENDET',
  'ABGERECHNET',
  'STORNIERT',
]

export default function Vertraege() {
  const [status, setzeStatus] = useState('IN_BELIEFERUNG')
  const [seite, setzeSeite] = useState(0)
  const erklaerungen = useStatuserklaerungen()
  const treffer = useAbruf<Seite<Vertragszeile>>(
    `/api/vertraege?status=${status}&seite=${seite}&groesse=25`,
  )

  return (
    <>
      <h1>Verträge</h1>
      <p className="untertitel">
        Der Übergang nach <em>In Belieferung</em> setzt die Zustimmung des
        Netzbetreibers voraus und wird von der Marktkommunikation ausgelöst.
        Lehnt er die Anmeldung ab oder hebt er die Zuordnung auf, steht der
        Vertrag auf <em>Anmeldung erforderlich</em>: er läuft weiter und ist neu
        anzumelden; den Grund nennt der Anmeldevorgang. <em>Beendet</em> ist ein
        Vertrag, wenn das Lieferende vorbei ist und der Netzbetreiber das Ende der
        Zuordnung bestätigt hat; mit der Schlussrechnung ist er <em>abgerechnet</em>.
      </p>

      <div className="leiste">
        <div className="feld">
          <label htmlFor="status">Status</label>
          <select
            id="status"
            value={status}
            title={erklaerungen[status]}
            onChange={(ereignis) => {
              setzeSeite(0)
              setzeStatus(ereignis.target.value)
            }}
          >
            {zustaende.map((wert) => (
              <option key={wert} value={wert} title={erklaerungen[wert]}>
                {wert}
              </option>
            ))}
          </select>
        </div>
      </div>
      <Statuslegende erklaerungen={erklaerungen} staende={zustaende} />

      <Karte>
        <Zustand
          laeuft={treffer.laeuft}
          fehler={treffer.fehler}
          leer={(treffer.daten?.inhalt ?? []).length === 0}
          leertext="Kein Vertrag in diesem Zustand."
        >
          <Tabelle
            spalten={[
              { name: 'Vertragsnummer' },
              { name: 'Kunde' },
              { name: 'Marktlokation' },
              { name: 'Lieferbeginn' },
              { name: 'Lieferende' },
            ]}
          >
            {(treffer.daten?.inhalt ?? []).map((vertrag) => (
              <tr key={vertrag.id}>
                <td>
                  <Link to={`/vertraege/${vertrag.vertragsnummer}`}>
                    {vertrag.vertragsnummer}
                  </Link>
                </td>
                <td>{vertrag.kundenname ?? '—'}</td>
                <td>{vertrag.marktlokation ?? '—'}</td>
                <td>{datum(vertrag.lieferbeginn)}</td>
                <td>{datum(vertrag.lieferende)}</td>
              </tr>
            ))}
          </Tabelle>

          {treffer.daten && treffer.daten.seitenzahl > 1 && (
            <div className="leiste" style={{ marginTop: 14, marginBottom: 0 }}>
              <button disabled={seite === 0} onClick={() => setzeSeite(seite - 1)}>
                Zurück
              </button>
              <span style={{ alignSelf: 'center', color: 'var(--gedaempft)', fontSize: 13 }}>
                Seite {treffer.daten.seite + 1} von {treffer.daten.seitenzahl}
              </span>
              <button
                disabled={seite + 1 >= treffer.daten.seitenzahl}
                onClick={() => setzeSeite(seite + 1)}
              >
                Weiter
              </button>
            </div>
          )}
        </Zustand>
      </Karte>
    </>
  )
}

/** Auch in Vertrags- und Kundenakte; beim Zeigen darauf steht die Erklaerung. */
export function Statusmerkmal({ status }: { status: string }) {
  const erklaerungen = useStatuserklaerungen()
  const art =
    status === 'IN_BELIEFERUNG'
      ? 'gut'
      : status === 'STORNIERT'
        ? 'fehler'
        : status === 'GEKUENDIGT' || status === 'ANMELDUNG_ERFORDERLICH'
          ? 'warnung'
          : 'neutral'
  return (
    <Merkmal art={art} titel={erklaerungen[status]}>
      {status}
    </Merkmal>
  )
}

/** Menge mit Einheit - hier gebuendelt, damit die Formatierung gleich bleibt. */
export function Jahresmenge({ wert }: { wert: string | number | null }) {
  return <>{menge(wert)}</>
}
